import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@systemeio/components';
import { DESKTOP_NAVBAR_DROPDOWN_PADDING_CLASS_NAME, DESKTOP_NAVBAR_ITEM_LAYOUT_CLASS_NAME } from 'shared/components/navbar/constants';
import type { MenuItem } from 'shared/components/navbar/types/menu-item';
import { isMenuLink } from 'shared/components/navbar/utils/is-menu-link';
import { useMetadataFromServer } from 'shared/hooks/use-metadata-from-server';
import { isSIO } from 'shared/utils/is-sio';
import { twJoin } from 'tailwind-merge';
interface DesktopMenuSkeletonItemsProps {
  items: MenuItem[];
}

/**
 * Placeholders are laid out from the real labels — same padding, font and chevron slot as
 * `DesktopMenuItem` — so the row keeps its rhythm in every locale and does not reflow when the
 * items arrive. Permissions are still unknown here, so every top-level item gets a placeholder.
 */
export function DesktopMenuSkeletonItems({
  items
}: DesktopMenuSkeletonItemsProps) {
  return items.map(item => <li key={isMenuLink(item) ? item.href : item.id}>
      <Skeleton className={twJoin(DESKTOP_NAVBAR_ITEM_LAYOUT_CLASS_NAME, isMenuLink(item) ? 'px-2' : DESKTOP_NAVBAR_DROPDOWN_PADDING_CLASS_NAME)}>
        <span className="invisible">{item.name}</span>
        {!isMenuLink(item) && <span className="size-5" />}
      </Skeleton>
    </li>);
}

/**
 * Mirrors the `ProfileMenu` trigger box: avatar `size-10`, chevron and the trigger padding. The
 * chevron gets its own mark — an empty slot there reads as trailing dead space rather than as part
 * of the control.
 */
export function ProfileSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="flex items-center gap-0.5 py-1 pr-1 pl-2"><Skeleton className="size-10 rounded-full" /><Skeleton className="size-5" /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Rendered until the client has measured the navbar. The server only sees the user agent, so it
 * cannot tell a narrow window on a desktop machine from a wide one: choosing the layout in JS makes
 * the browser paint the desktop navbar and collapse it to the mobile one on hydration. This
 * placeholder resolves the same 1024px breakpoint in CSS, so the first paint already matches the
 * viewport and the measured layout replaces it without a visible jump.
 */
export function NavbarSkeleton(t0) {
  const $ = _c(15);
  const {
    items
  } = t0;
  const {
    data: metadata
  } = useMetadataFromServer();
  const t1 = metadata?.hostOnServer;
  let t2;
  if ($[0] !== t1) {
    t2 = isSIO(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isSio = t2;
  let t3;
  if ($[2] !== items) {
    t3 = <ul className="max-desktop:hidden flex min-w-0 items-center gap-1"><DesktopMenuSkeletonItems items={items} /></ul>;
    $[2] = items;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isSio) {
    t4 = isSio && <Skeleton className="max-desktop:hidden size-8 rounded-md" />;
    $[4] = isSio;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="size-8 rounded-md" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="max-desktop:hidden"><ProfileSkeleton /></span>;
    t7 = <Skeleton className="max-desktop:hidden h-8 w-13 rounded-md" />;
    t8 = <Skeleton className="desktop:hidden size-8 rounded-md" />;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t4) {
    t9 = <div className="ml-auto flex shrink-0 items-center gap-1">{t4}{t5}{t6}{t7}{t8}</div>;
    $[10] = t4;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t3 || $[13] !== t9) {
    t10 = <>{t3}{t9}</>;
    $[12] = t3;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}